/**
 * Snapshot buffering and interpolation for remote entities.
 *
 * This is the file the lessons doc is really about. Three rules, each of which
 * was learned the expensive way:
 *
 *  1. Render remote entities at `serverNow - INTERP_DELAY_MS`, interpolating
 *     between the two snapshots that straddle that time. Lerping toward the
 *     newest packet as it arrives looks fine on localhost and stutters
 *     everywhere else, because packets arrive at irregular intervals and each
 *     one restarts the lerp from wherever the entity happened to have got to.
 *
 *  2. On starvation, hold the last known pose. Do not extrapolate. A wrong
 *     guess that has to be visibly corrected reads worse than a brief pause.
 *
 *  3. Interpolate yaw the short way round. Linear interpolation spins the long
 *     way whenever the value crosses the +/-PI seam.
 *
 * The local player does NOT go through here — it is predicted and rendered
 * live. Delaying your own input to be consistent with remote entities costs
 * responsiveness and buys nothing.
 */

import { INTERP_STARVE_MS, lerp, lerpAngle } from "@prop-hunt/shared";

export interface EntitySample {
  /** Server simulation time this sample describes. */
  time: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  propId: number;
  flags: number;
}

export interface Pose {
  x: number;
  y: number;
  z: number;
  yaw: number;
  propId: number;
  flags: number;
  /** True when we ran off the end of the buffer and are holding a stale pose. */
  starved: boolean;
  /** How far past the newest sample the render time is, in ms. */
  starvedForMs: number;
}

/** ~1.5s at 20Hz. Generous enough to ride out a stall, bounded enough to not leak. */
const MAX_SAMPLES = 32;

export class EntityTimeline {
  private readonly samples: EntitySample[] = [];

  push(sample: EntitySample): void {
    const newest = this.samples[this.samples.length - 1];
    // Defensive: WebSocket is ordered, so this should not fire. "Should not
    // fire" is exactly the assumption that produces the bug where a
    // redelivered packet rewinds an entity, so the guard stays.
    if (newest !== undefined && sample.time <= newest.time) return;

    this.samples.push(sample);
    if (this.samples.length > MAX_SAMPLES) this.samples.shift();
  }

  get depth(): number {
    return this.samples.length;
  }

  get newestTime(): number {
    return this.samples[this.samples.length - 1]?.time ?? 0;
  }

  /**
   * Pose at `renderTime` on the server timeline, or null if nothing has
   * arrived yet.
   */
  sample(renderTime: number): Pose | null {
    const count = this.samples.length;
    if (count === 0) return null;

    const oldest = this.samples[0]!;
    const newest = this.samples[count - 1]!;

    // Over-buffered: render time is still behind the oldest sample we hold.
    // Happens for a moment after joining. Show the oldest rather than nothing.
    if (renderTime <= oldest.time) {
      return poseOf(oldest, false, 0);
    }

    // Starved: render time has run past the newest sample. HOLD, never
    // extrapolate.
    if (renderTime >= newest.time) {
      const behind = renderTime - newest.time;
      return poseOf(newest, behind > INTERP_STARVE_MS, behind);
    }

    // Walk back from the newest — the straddling pair is almost always the
    // last one, so this is O(1) in practice.
    for (let i = count - 1; i > 0; i--) {
      const b = this.samples[i]!;
      const a = this.samples[i - 1]!;
      if (renderTime >= a.time && renderTime <= b.time) {
        const span = b.time - a.time;
        const t = span > 0 ? (renderTime - a.time) / span : 0;
        return {
          x: lerp(a.x, b.x, t),
          y: lerp(a.y, b.y, t),
          z: lerp(a.z, b.z, t),
          // Shortest-path, so crossing the seam does not spin the model round.
          yaw: lerpAngle(a.yaw, b.yaw, t),
          // Discrete fields take the newer sample rather than blending —
          // there is no meaningful halfway between two prop identities.
          propId: b.propId,
          flags: b.flags,
          starved: false,
          starvedForMs: 0,
        };
      }
    }

    return poseOf(newest, false, 0);
  }

  /** Drop samples that can no longer be needed to interpolate `renderTime`. */
  prune(renderTime: number): void {
    // Keep the last sample at or before renderTime — it is the left-hand side
    // of the straddling pair. Dropping it strands the interpolator.
    while (this.samples.length > 2 && this.samples[1]!.time <= renderTime) {
      this.samples.shift();
    }
  }
}

function poseOf(sample: EntitySample, starved: boolean, starvedForMs: number): Pose {
  return {
    x: sample.x,
    y: sample.y,
    z: sample.z,
    yaw: sample.yaw,
    propId: sample.propId,
    flags: sample.flags,
    starved,
    starvedForMs,
  };
}
